// Admin → Super admins (2026-09-25). Who can run Fleetspoke: add one (invite-user, kind platform_admin — the
// account is created if needed; nothing is sent, they are told offline — 2026-09-26), remove one (never yourself;
// the database keeps the last one and closes their open support visit), and the log of both. A removal emails
// every super admin.
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LastActive } from '../../components/LastActive';
import { Loading, LoadError, Page } from '../../components/Page';
import {
  adminKeys,
  inviteSuperAdmin,
  type SuperAdmin,
  useRemoveSuperAdmin,
  useSuperAdminChanges,
  useSuperAdmins,
} from '../../data/admin';
import { dateTime } from '../../lib/time';
import { blankManager, ManagerFields, useInviteError } from './CompaniesPage';

const TZ = 'Asia/Kuala_Lumpur';

function AddDialog({ onClose }: { onClose(): void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const inviteError = useInviteError();
  const [person, setPerson] = useState(blankManager);
  const [state, setState] = useState<'idle' | 'busy' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setState('busy');
    const code = await inviteSuperAdmin(person);
    setError(inviteError(code));
    setState(code ? 'idle' : 'done');
    if (!code) {
      void queryClient.invalidateQueries({ queryKey: adminKeys.superAdmins });
      void queryClient.invalidateQueries({ queryKey: adminKeys.superAdminChanges });
    }
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <form onSubmit={(e) => void submit(e)}>
        <DialogTitle>{t('admin.super.add')}</DialogTitle>
        <DialogContent dividers>
          {state === 'done' ? (
            <Alert severity="success">{t('admin.super.added')}</Alert>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t('admin.super.add_warning')}
              </Alert>
              <ManagerFields
                value={person}
                onChange={setPerson}
                nameLabel={t('admin.super.name')}
              />
            </>
          )}
          {error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.close')}</Button>
          {state !== 'done' ? (
            <Button
              type="submit"
              variant="contained"
              disabled={state === 'busy' || !person.phone.trim()}
              data-testid="super-add-confirm"
            >
              {t('admin.super.add')}
            </Button>
          ) : null}
        </DialogActions>
      </form>
    </Dialog>
  );
}

function RemoveDialog({ admin, onClose }: { admin: SuperAdmin; onClose(): void }) {
  const { t } = useTranslation();
  const remove = useRemoveSuperAdmin();
  const name = admin.full_name ?? admin.phone ?? admin.email ?? '—';
  const code = (remove.error as { code?: string } | null)?.code;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('admin.super.remove_title', { name })}</DialogTitle>
      <DialogContent>
        <DialogContentText>{t('admin.super.remove_body')}</DialogContentText>
        {remove.error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {code === '23514' ? t('admin.super.err_last') : (remove.error as Error).message}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          color="error"
          variant="contained"
          disabled={remove.isPending}
          onClick={() => remove.mutate(admin.user_id, { onSuccess: onClose })}
          data-testid="super-remove-confirm"
        >
          {t('admin.super.remove')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function AdminSuperAdminsPage() {
  const { t, i18n } = useTranslation();
  const admins = useSuperAdmins();
  const changes = useSuperAdminChanges();
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<SuperAdmin | null>(null);

  return (
    <Page
      title={t('admin.super.title')}
      actions={
        <Button variant="contained" onClick={() => setAdding(true)}>
          {t('admin.super.add')}
        </Button>
      }
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('admin.super.help')}
      </Typography>
      {admins.isLoading ? <Loading /> : null}
      {admins.error ? <LoadError error={admins.error} /> : null}
      {admins.data ? (
        <Paper variant="outlined" sx={{ overflowX: 'auto', mb: 4 }}>
          <Table size="small" data-testid="super-admins">
            <TableHead>
              <TableRow>
                <TableCell>{t('settings.users.name')}</TableCell>
                <TableCell>{t('settings.users.phone')}</TableCell>
                <TableCell>{t('settings.users.email')}</TableCell>
                <TableCell>{t('settings.users.last_active')}</TableCell>
                <TableCell>{t('admin.super.col_added')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {admins.data.map((a) => (
                <TableRow key={a.user_id} hover>
                  <TableCell>
                    {a.full_name ?? '—'}
                    {a.is_me ? (
                      <Chip size="small" sx={{ ml: 1 }} label={t('admin.super.you')} />
                    ) : null}
                  </TableCell>
                  <TableCell>{a.phone ? `+${a.phone.replace(/^\+/, '')}` : '—'}</TableCell>
                  <TableCell>{a.email ?? '—'}</TableCell>
                  <TableCell>
                    <LastActive
                      activeAt={a.last_active_at}
                      signedInAt={a.last_sign_in_at}
                      timeZone={TZ}
                    />
                  </TableCell>
                  <TableCell>
                    {dateTime(a.added_at, i18n.language, TZ)}
                    {a.added_by_name ? ` · ${a.added_by_name}` : ''}
                  </TableCell>
                  <TableCell align="right">
                    {a.is_me ? null : (
                      <Button size="small" color="error" onClick={() => setRemoving(a)}>
                        {t('admin.super.remove')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      ) : null}

      <Typography variant="h6" sx={{ mb: 1 }}>
        {t('admin.super.changes')}
      </Typography>
      {changes.data && !changes.data.length ? (
        <Typography color="text.secondary">{t('admin.super.no_changes')}</Typography>
      ) : null}
      {changes.data?.length ? (
        <Box component="ul" sx={{ pl: 2, m: 0 }} data-testid="super-admin-changes">
          {changes.data.map((c) => (
            <Typography component="li" variant="body2" key={c.id} sx={{ mb: 0.5 }}>
              {dateTime(c.at, i18n.language, TZ)} —{' '}
              {t(`admin.super.change_${c.action}`, {
                name: c.subject_name ?? '—',
                by: c.by_name ?? '—',
              })}
            </Typography>
          ))}
        </Box>
      ) : null}

      {adding ? <AddDialog onClose={() => setAdding(false)} /> : null}
      {removing ? <RemoveDialog admin={removing} onClose={() => setRemoving(null)} /> : null}
    </Page>
  );
}
